"use client";

import { ArrowLeft, Wine, Thermometer, MapPin } from 'lucide-react';
import Link from 'next/link';
import type { EducationGrapesPageContent } from '@/lib/page-content-types';

const redGrapes = [
  {
    name: 'Cabernet Sauvignon',
    origin: 'Bordeaux, France',
    description: 'The backbone of Israeli premium wine. Thrives in high-elevation sites with limestone soils, producing structured, age-worthy wines with cassis, tobacco, and Mediterranean herb notes.',
    characteristics: {
      body: 'Full',
      tannins: 'High',
      acidity: 'Medium-High',
      alcohol: '13.5-15%'
    },
    climate: 'Cool to moderate sites at elevation',
    soils: 'Limestone, volcanic basalt, terra rossa',
    regions: ['Golan Heights', 'Galilee', 'Judean Hills'],
    flavors: ['Blackcurrant', 'Cedar', 'Tobacco', 'Mediterranean herbs', 'Dark chocolate'],
    agingPotential: '10-20+ years for premium examples',
    foodPairings: ['Grilled lamb', 'Beef brisket', 'Hard cheeses', 'Mushroom dishes']
  },
  {
    name: 'Syrah',
    origin: 'Rhône Valley, France',
    description: 'Increasingly important in Israeli wine. Cool-climate sites produce elegant, peppery wines with refined tannins and beautiful aging potential.',
    characteristics: {
      body: 'Medium-Full',
      tannins: 'Medium-High',
      acidity: 'Medium-High',
      alcohol: '13-14.5%'
    },
    climate: 'Cool to moderate elevation sites',
    soils: 'Limestone, granite, volcanic',
    regions: ['Galilee', 'Judean Hills', 'Golan Heights'],
    flavors: ['Black pepper', 'Blackberry', 'Olive', 'Smoked meat', 'Violet'],
    agingPotential: '8-15 years',
    foodPairings: ['Grilled meats', 'Game', 'Eggplant dishes', 'Shawarma']
  },
  {
    name: 'Merlot',
    origin: 'Bordeaux, France',
    description: 'Essential blending partner and compelling varietal. Israeli Merlot shows ripe plum fruit with soft tannins and approachability.',
    characteristics: {
      body: 'Medium-Full',
      tannins: 'Medium',
      acidity: 'Medium',
      alcohol: '13.5-14.5%'
    },
    climate: 'Moderate climate sites',
    soils: 'Clay-limestone, terra rossa',
    regions: ['Golan Heights', 'Galilee', 'Judean Hills'],
    flavors: ['Plum', 'Cherry', 'Chocolate', 'Dried herbs', 'Mocha'],
    agingPotential: '5-12 years',
    foodPairings: ['Roasted chicken', 'Pork', 'Tomato-based dishes', 'Soft cheeses']
  },
  {
    name: 'Carignan',
    origin: 'Spain/Mediterranean',
    description: 'Old-vine Carignan represents Israel\'s heritage. These ancient vines produce rustic, earthy wines with authentic Mediterranean character.',
    characteristics: {
      body: 'Medium-Full',
      tannins: 'High',
      acidity: 'High',
      alcohol: '13-14.5%'
    },
    climate: 'Warm, dry sites',
    soils: 'Limestone, terra rossa',
    regions: ['Judean Hills', 'Shomron'],
    flavors: ['Red berries', 'Earth', 'Dried herbs', 'Black tea', 'Leather'],
    agingPotential: '8-15 years from old vines',
    foodPairings: ['Grilled vegetables', 'Stews', 'Middle Eastern mezze', 'Aged cheeses']
  },
  {
    name: 'Petit Verdot',
    origin: 'Bordeaux, France',
    description: 'Thrives in Israel\'s warm climate. Used in blends for color, tannin, and structure. Increasingly made as a varietal wine.',
    characteristics: {
      body: 'Full',
      tannins: 'Very High',
      acidity: 'High',
      alcohol: '14-15%'
    },
    climate: 'Warm sites with long growing season',
    soils: 'Well-drained soils',
    regions: ['Negev', 'Judean Hills', 'Galilee'],
    flavors: ['Violet', 'Blackberry', 'Graphite', 'Spice', 'Tar'],
    agingPotential: '10-20 years',
    foodPairings: ['Braised meats', 'Game', 'Strong cheeses', 'Grilled lamb']
  },
  {
    name: 'Grenache',
    origin: 'Spain/Rhône',
    description: 'Mediterranean variety gaining traction. Produces wines with red fruit, spice, and approachable tannins.',
    characteristics: {
      body: 'Medium-Full',
      tannins: 'Medium',
      acidity: 'Medium',
      alcohol: '14-15%'
    },
    climate: 'Warm, dry sites',
    soils: 'Sandy, well-drained',
    regions: ['Negev', 'Judean Hills', 'Sharon'],
    flavors: ['Strawberry', 'White pepper', 'Herbs', 'Garrigue', 'Orange peel'],
    agingPotential: '5-10 years',
    foodPairings: ['Grilled fish', 'Lamb kebabs', 'Vegetable tagines', 'Soft cheeses']
  }
];

const whiteGrapes = [
  {
    name: 'Chardonnay',
    origin: 'Burgundy, France',
    description: 'Cool-climate Israeli Chardonnay shows elegance and restraint. Mineral-driven with citrus and stone fruit, often with judicious oak aging.',
    characteristics: {
      body: 'Medium-Full',
      acidity: 'Medium-High',
      alcohol: '12.5-14%'
    },
    climate: 'Cool, high-elevation sites',
    soils: 'Limestone, volcanic',
    regions: ['Golan Heights', 'Galilee', 'Judean Hills'],
    flavors: ['Lemon', 'Green apple', 'Mineral', 'Toast', 'Hazelnut'],
    agingPotential: '5-8 years for premium wines',
    foodPairings: ['Grilled fish', 'Roasted chicken', 'Creamy pastas', 'Soft cheeses']
  },
  {
    name: 'Sauvignon Blanc',
    origin: 'Loire Valley/Bordeaux, France',
    description: 'High-elevation sites produce fresh, aromatic Sauvignon Blanc with citrus, herbs, and mineral character. Unoaked for purity.',
    characteristics: {
      body: 'Light-Medium',
      acidity: 'High',
      alcohol: '12-13.5%'
    },
    climate: 'Cool sites with good diurnal variation',
    soils: 'Limestone, volcanic',
    regions: ['Golan Heights', 'Galilee'],
    flavors: ['Grapefruit', 'Lime', 'Fresh herbs', 'Passion fruit', 'Flint'],
    agingPotential: '2-4 years, drink young',
    foodPairings: ['Goat cheese', 'Salads', 'Seafood', 'Herb-crusted fish']
  },
  {
    name: 'Viognier',
    origin: 'Rhône Valley, France',
    description: 'Aromatic Mediterranean variety producing full-bodied whites with stone fruit and floral notes. Increasingly popular in Israel.',
    characteristics: {
      body: 'Medium-Full',
      acidity: 'Medium',
      alcohol: '13.5-14.5%'
    },
    climate: 'Warm sites with cool nights',
    soils: 'Granite, limestone',
    regions: ['Galilee', 'Judean Hills', 'Shomron'],
    flavors: ['Apricot', 'Peach', 'Honeysuckle', 'Tangerine', 'Ginger'],
    agingPotential: '3-5 years',
    foodPairings: ['Spiced chicken', 'Asian cuisine', 'Rich fish dishes', 'Soft cheeses']
  },
  {
    name: 'Roussanne',
    origin: 'Rhône Valley, France',
    description: 'Rare but promising Mediterranean variety. Produces structured whites with honeyed fruit and excellent aging potential.',
    characteristics: {
      body: 'Full',
      acidity: 'Medium-High',
      alcohol: '13.5-14.5%'
    },
    climate: 'Warm sites',
    soils: 'Limestone, rocky soils',
    regions: ['Judean Hills', 'Galilee'],
    flavors: ['Pear', 'Honey', 'Herbal tea', 'Almond', 'White flowers'],
    agingPotential: '5-10 years',
    foodPairings: ['Roasted poultry', 'Creamy dishes', 'Aged cheeses', 'Nuts']
  },
  {
    name: 'Gewürztraminer',
    origin: 'Alsace, France',
    description: 'Aromatic variety finding success in cool Israeli sites. Produces distinctive wines with lychee, rose petal, and spice.',
    characteristics: {
      body: 'Medium-Full',
      acidity: 'Medium',
      alcohol: '13-14.5%'
    },
    climate: 'Cool elevation sites',
    soils: 'Volcanic, limestone',
    regions: ['Golan Heights', 'Galilee'],
    flavors: ['Lychee', 'Rose', 'Ginger', 'Tropical fruit', 'Sweet spice'],
    agingPotential: '3-6 years',
    foodPairings: ['Asian cuisine', 'Spicy foods', 'Duck', 'Blue cheese']
  },
  {
    name: 'Grenache Blanc',
    origin: 'Rhône/Spain',
    description: 'Mediterranean white variety gaining attention. Full-bodied with ripe fruit and good structure.',
    characteristics: {
      body: 'Medium-Full',
      acidity: 'Medium',
      alcohol: '13.5-14.5%'
    },
    climate: 'Warm, dry sites',
    soils: 'Limestone, sandy soils',
    regions: ['Judean Hills', 'Negev'],
    flavors: ['White peach', 'Meyer lemon', 'Herbs', 'Almond', 'White pepper'],
    agingPotential: '3-5 years',
    foodPairings: ['Grilled fish', 'Mediterranean vegetables', 'Chicken', 'Soft cheeses']
  }
];

function toSlug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function GrapesPage({ content }: { content?: EducationGrapesPageContent | null }) {
  const redGrapesData = content?.redGrapes?.length ? content.redGrapes : redGrapes;
  const whiteGrapesData = content?.whiteGrapes?.length ? content.whiteGrapes : whiteGrapes;

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Back navigation */}
      <div style={{ backgroundColor: 'var(--linen)', borderBottom: '1px solid var(--stone)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16 py-8">
          <Link 
            href="/education"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{ 
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--charcoal)',
              fontWeight: 400
            }}
          >
            <ArrowLeft size={16} />
            Back to Education
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-4xl">
              <h1 
                className="mb-8"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-5xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Grape Varieties
              </h1>
              
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-xl)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '720px'
                }}
              >
                Israel&apos;s diverse terroirs showcase international varieties alongside Mediterranean heritage grapes, each expressing unique character through altitude, soil, and climate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Red Grapes */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <Wine size={28} style={{ color: 'var(--oxblood)' }} />
                <h2 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-3xl)',
                    fontWeight: 300,
                    lineHeight: 'var(--line-height-tight)',
                    color: 'var(--charcoal)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  Red Varieties
                </h2>
              </div>
            </div>

            <div className="space-y-16">
              {redGrapesData.map((grape) => (
                <div 
                  key={grape.name}
                  className="p-12"
                  style={{ backgroundColor: 'var(--paper)' }}
                >
                  <div className="grid lg:grid-cols-3 gap-12">
                    {/* Main Info */}
                    <div className="lg:col-span-2">
                      <div className="mb-3">
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--oxblood)',
                            fontWeight: 400,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase'
                          }}
                        >
                          {grape.origin}
                        </span>
                      </div>

                      <h3 className="mb-6">
                        <Link
                          href={`/education/grapes/${toSlug(grape.name)}`}
                          className="transition-opacity duration-200 hover:opacity-60"
                          style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: 'var(--text-3xl)',
                            fontWeight: 300,
                            color: 'var(--charcoal)'
                          }}
                        >
                          {grape.name}
                        </Link>
                      </h3>

                      <p 
                        className="mb-8"
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-base)',
                          lineHeight: 'var(--line-height-body)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {grape.description}
                      </p>

                      {/* Characteristics Grid */}
                      <div className="grid grid-cols-2 gap-4 mb-8 p-6" style={{ backgroundColor: 'var(--linen)' }}>
                        <div>
                          <span 
                            className="block mb-1"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em'
                            }}
                          >
                            BODY
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {grape.characteristics.body}
                          </span>
                        </div>
                        <div>
                          <span 
                            className="block mb-1"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em'
                            }}
                          >
                            TANNINS
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {grape.characteristics.tannins}
                          </span>
                        </div>
                        <div>
                          <span 
                            className="block mb-1"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em'
                            }}
                          >
                            ACIDITY
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {grape.characteristics.acidity}
                          </span>
                        </div>
                        <div>
                          <span 
                            className="block mb-1"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em'
                            }}
                          >
                            ALCOHOL
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {grape.characteristics.alcohol}
                          </span>
                        </div>
                      </div>

                      {/* Flavor Profile */}
                      <div className="mb-8">
                        <h4 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Flavor Profile
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {grape.flavors.map((flavor) => (
                            <span 
                              key={flavor}
                              className="px-3 py-1"
                              style={{ 
                                backgroundColor: 'var(--linen)',
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-xs)',
                                color: 'var(--charcoal)',
                                fontWeight: 400
                              }}
                            >
                              {flavor}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Food Pairings */}
                      <div>
                        <h4 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Food Pairings
                        </h4>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.foodPairings.join(' • ')}
                        </p>
                      </div>
                    </div>

                    {/* Side Info */}
                    <div>
                      <div className="mb-8">
                        <div className="flex items-center gap-2 mb-3">
                          <Thermometer size={16} style={{ color: 'var(--oxblood)' }} />
                          <h4 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase'
                            }}
                          >
                            Climate
                          </h4>
                        </div>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.climate}
                        </p>
                      </div>

                      <div className="mb-8">
                        <div className="flex items-center gap-2 mb-3">
                          <MapPin size={16} style={{ color: 'var(--oxblood)' }} />
                          <h4 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase'
                            }}
                          >
                            Preferred Soils
                          </h4>
                        </div>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.soils}
                        </p>
                      </div>

                      <div className="mb-8">
                        <h4 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Key Regions
                        </h4>
                        <ul className="space-y-2">
                          {grape.regions.map((region) => (
                            <li 
                              key={region}
                              style={{ 
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-sm)',
                                color: 'var(--slate)',
                                fontWeight: 300
                              }}
                            >
                              • {region}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="p-4" style={{ backgroundColor: 'var(--linen)' }}>
                        <h4 
                          className="mb-2"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Aging Potential
                        </h4>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.agingPotential}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* White Grapes */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <Wine size={28} style={{ color: 'var(--copper)' }} />
                <h2 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-3xl)',
                    fontWeight: 300,
                    lineHeight: 'var(--line-height-tight)',
                    color: 'var(--charcoal)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  White Varieties
                </h2>
              </div>
            </div>

            <div className="space-y-16">
              {whiteGrapesData.map((grape) => (
                <div 
                  key={grape.name}
                  className="p-12"
                  style={{ backgroundColor: 'var(--linen)' }}
                >
                  <div className="grid lg:grid-cols-3 gap-12">
                    {/* Main Info */}
                    <div className="lg:col-span-2">
                      <div className="mb-3">
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--copper)',
                            fontWeight: 400,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase'
                          }}
                        >
                          {grape.origin}
                        </span>
                      </div>

                      <h3 className="mb-6">
                        <Link
                          href={`/education/grapes/${toSlug(grape.name)}`}
                          className="transition-opacity duration-200 hover:opacity-60"
                          style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: 'var(--text-3xl)',
                            fontWeight: 300,
                            color: 'var(--charcoal)'
                          }}
                        >
                          {grape.name}
                        </Link>
                      </h3>

                      <p 
                        className="mb-8"
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-base)',
                          lineHeight: 'var(--line-height-body)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {grape.description}
                      </p>

                      {/* Characteristics Grid */}
                      <div className="grid grid-cols-3 gap-4 mb-8 p-6" style={{ backgroundColor: 'var(--paper)' }}>
                        <div>
                          <span 
                            className="block mb-1"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em'
                            }}
                          >
                            BODY
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {grape.characteristics.body}
                          </span>
                        </div>
                        <div>
                          <span 
                            className="block mb-1"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em'
                            }}
                          >
                            ACIDITY
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {grape.characteristics.acidity}
                          </span>
                        </div>
                        <div>
                          <span 
                            className="block mb-1"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em'
                            }}
                          >
                            ALCOHOL
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {grape.characteristics.alcohol}
                          </span>
                        </div>
                      </div>

                      {/* Flavor Profile */}
                      <div className="mb-8">
                        <h4 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Flavor Profile
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {grape.flavors.map((flavor) => (
                            <span 
                              key={flavor}
                              className="px-3 py-1"
                              style={{ 
                                backgroundColor: 'var(--paper)',
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-xs)',
                                color: 'var(--charcoal)',
                                fontWeight: 400
                              }}
                            >
                              {flavor}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Food Pairings */}
                      <div>
                        <h4 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Food Pairings
                        </h4>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.foodPairings.join(' • ')}
                        </p>
                      </div>
                    </div>

                    {/* Side Info */}
                    <div>
                      <div className="mb-8">
                        <div className="flex items-center gap-2 mb-3">
                          <Thermometer size={16} style={{ color: 'var(--copper)' }} />
                          <h4 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase'
                            }}
                          >
                            Climate
                          </h4>
                        </div>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.climate}
                        </p>
                      </div>

                      <div className="mb-8">
                        <div className="flex items-center gap-2 mb-3">
                          <MapPin size={16} style={{ color: 'var(--copper)' }} />
                          <h4 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--charcoal)',
                              fontWeight: 500,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase'
                            }}
                          >
                            Preferred Soils
                          </h4>
                        </div>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.soils}
                        </p>
                      </div>

                      <div className="mb-8">
                        <h4 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Key Regions
                        </h4>
                        <ul className="space-y-2">
                          {grape.regions.map((region) => (
                            <li 
                              key={region}
                              style={{ 
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-sm)',
                                color: 'var(--slate)',
                                fontWeight: 300
                              }}
                            >
                              • {region}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="p-4" style={{ backgroundColor: 'var(--paper)' }}>
                        <h4 
                          className="mb-2"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Aging Potential
                        </h4>
                        <p 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {grape.agingPotential}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
